import {
  getImages,
  uploadCommunityImage,
  uploadFarmerImage,
} from '@/adapters/master';
import { IDataModal } from '@/interfaces/data-modal';
import {
  ICommunityImages,
  IFarmerImages,
  IUploadCommunityImageReq,
  IUploadFarmerImageReq,
  IUploadImageRes,
} from '@/interfaces/master';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Badge,
  Flex,
  Form,
  FormProps,
  Modal,
  notification,
  Radio,
  Select,
  Table,
  TableColumnsType,
  Button,
  Upload,
} from 'antd';
import { useEffect, useMemo, useState, useCallback, Suspense } from 'react';
import { uniqueId } from 'lodash';

const _modal: IDataModal<IUploadImageRes> = {
  open: false,
};

const UploadImagesPage: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<
    (IFarmerImages | ICommunityImages)[]
  >([]);
  const [action, setAction] = useState<'farmer_upload' | 'community_upload'>(
    'farmer_upload',
  );
  const [selected, setSelected] = useState<
    (IFarmerImages | ICommunityImages)[]
  >([]);
  // const [modal, setModal] = useState<
  //   IDataModal<IFarmerImages | ICommunityImages>
  // >({ ..._modal });
  const [form] = Form.useForm();
  const [tab, setTab] = useState<'farmer_upload' | 'community_upload'>(
    'farmer_upload',
  );

  // const { data, refetch } = useQuery<IBrandRes, Error>({
  //   queryKey: ['search-brand-list'],
  //   queryFn: searchBrands,
  //   retry: 0,
  // });
  const { data, refetch } = useQuery<IUploadImageRes, Error>({
    queryKey: ['get-images'],
    queryFn: getImages,
    retry: 0,
  });

  const actionImagesMutation = useMutation<
    IUploadImageRes,
    Error,
    IUploadFarmerImageReq | IUploadCommunityImageReq
  >({
    mutationFn: async payload => {
      if (tab === 'farmer_upload') {
        return await uploadFarmerImage(payload as IUploadFarmerImageReq);
      }
      return await uploadCommunityImage(payload as IUploadCommunityImageReq);
    },

    mutationKey:
      tab === 'farmer_upload'
        ? ['upload-farmer-images']
        : ['upload-community-images'],

    onSuccess: data => {
      // ตอนนี้ data เป็น IUploadImageRes แบบ type-safe แล้ว
      if (tab === 'farmer_upload') {
        notification.success({ message: 'Sync Farmer Images สำเร็จ' });
      } else {
        notification.success({ message: 'Sync Community Images สำเร็จ' });
      }
      refetch();
    },

    onError: () => {
      notification.error({ message: 'Upload Images ไม่สำเร็จ' });
    },
  });

  const handleSyncSelectedImages = async (selectedRows: any) => {
    if (tab === 'farmer_upload') {
      await actionImagesMutation.mutateAsync(
        selectedRows as IUploadFarmerImageReq,
      );
    } else {
      await actionImagesMutation.mutateAsync(
        selectedRows as IUploadCommunityImageReq,
      );
    }
  };

  // const actionDeleteImages = useMutation({
  //   mutationFn: async (data: Partial<IImages>) => await deleteImages(data),
  //   mutationKey: ['delete-Images'],
  //   onSuccess: data => {
  //     if (data.status === 'OK' && tab === 'update') {
  //       notification.success({ message: 'Delete Images สำเร็จ' });
  //       refetch();
  //     } else {
  //       notification.error({ message: 'Delete Images ไม่สำเร็จ' });
  //     }
  //   },
  //   onError: error => {
  //     console.log('error', error);
  //   },
  // });

  // const handleSyncTOERP = async () => {
  //   await actionTOERP.mutateAsync();
  // };

  // const handleDeleteImages = async (selectedRows: any) => {
  //   await actionDeleteImages.mutateAsync(selectedRows);
  // };

  // const handleUpdateImagesCode = useCallback((item: IImages) => {
  //   setModal({
  //     open: true,
  //     item: item,
  //   });
  // }, []);

  const columnsFarmer = useMemo(
    () =>
      [
        {
          title: 'ลำดับ',
          dataIndex: '',
          width: 40,
          align: 'center' as const,
          className: 'text-bold',
          render: (_: any, __: IFarmerImages, i: number) => (
            <div className="font-weight-bold">{i + 1}</div>
          ),
        },
        {
          title: 'รหัส',
          dataIndex: 'FRM_CODE',
          defaultSortOrder: 'ascend',
          sorter: (a, b) =>
            Number(a.FRM_CODE?.replace('FRM', '').replace('-', '')) -
            Number(b.FRM_CODE?.replace('FRM', '').replace('-', '')),
          filterSearch: true,
          filters: Array.from(
            new Set(
              selected
                .filter((i): i is IFarmerImages => 'FRM_CODE' in i)
                .map(item => item.FRM_CODE),
            ),
          )
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.FRM_CODE?.startsWith(value as string);
          },
        },
        {
          title: 'ชื่อ',
          dataIndex: 'FRM_FNAME',
          sorter: (a, b) =>
            Number(a.FRM_FNAME?.replace(/\D/g, '').replace('-', '')) -
            Number(b.FRM_FNAME?.replace(/\D/g, '').replace('-', '')),
          filterSearch: true,
          filters: Array.from(
            new Set(
              selected
                .filter((i): i is IFarmerImages => 'FRM_FNAME' in i)
                .map(item => item.FRM_FNAME),
            ),
          )
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.FRM_FNAME?.startsWith(value as string);
          },
        },
        {
          title: 'นามสกุล',
          dataIndex: 'FRM_LNAME',
          sorter: (a, b) =>
            Number(a.FRM_LNAME?.replace(/\D/g, '').replace('-', '')) -
            Number(b.FRM_LNAME?.replace(/\D/g, '').replace('-', '')),
          filterSearch: true,
          filters: Array.from(
            new Set(
              selected
                .filter((i): i is IFarmerImages => 'FRM_LNAME' in i)
                .map(item => item.FRM_LNAME),
            ),
          )
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.FRM_LNAME?.startsWith(value as string);
          },
        },
        {
          title: 'ชื่อรูป',
          dataIndex: 'FRM_PF',
          sorter: (a, b) =>
            Number(a.FRM_PF?.replace(/\D/g, '').replace('-', '')) -
            Number(b.FRM_PF?.replace(/\D/g, '').replace('-', '')),
          filterSearch: true,
          filters: Array.from(
            new Set(
              selected
                .filter((i): i is IFarmerImages => 'FRM_PF' in i)
                .map(item => item.FRM_PF),
            ),
          )
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.FRM_PF?.startsWith(value as string);
          },
        },
      ] as TableColumnsType<IFarmerImages>,
    [tab, selected],
  );

  const columnsCommunity = useMemo(
    () =>
      [
        {
          title: 'ลำดับ',
          dataIndex: '',
          width: 40,
          align: 'center' as const,
          className: 'text-bold',
          render: (_: any, __: ICommunityImages, i: number) => (
            <div className="font-weight-bold">{i + 1}</div>
          ),
        },
        {
          title: 'รหัส',
          dataIndex: 'SI_SITE',
          defaultSortOrder: 'ascend',
          sorter: (a, b) =>
            Number(a.SI_SITE?.replace('FRM', '').replace('-', '')) -
            Number(b.SI_SITE?.replace('FRM', '').replace('-', '')),
          filterSearch: true,
          filters: Array.from(
            new Set(
              selected
                .filter((i): i is ICommunityImages => 'SI_SITE' in i)
                .map(item => item.SI_SITE),
            ),
          )
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.SI_SITE?.startsWith(value as string);
          },
        },
        {
          title: 'ชื่อ',
          dataIndex: 'SI_DESC',
          sorter: (a, b) =>
            Number(a.SI_DESC?.replace(/\D/g, '').replace('-', '')) -
            Number(b.SI_DESC?.replace(/\D/g, '').replace('-', '')),
          filterSearch: true,
          filters: Array.from(
            new Set(
              selected
                .filter((i): i is ICommunityImages => 'SI_DESC' in i)
                .map(item => item.SI_DESC),
            ),
          )
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.SI_DESC?.startsWith(value as string);
          },
        },
        {
          title: 'ชื่อรูป',
          dataIndex: 'SI_PIC',
          sorter: (a, b) =>
            Number(a.SI_PIC?.replace(/\D/g, '').replace('-', '')) -
            Number(b.SI_PIC?.replace(/\D/g, '').replace('-', '')),
          filterSearch: true,
          filters: Array.from(
            new Set(
              selected
                .filter((i): i is IFarmerImages => 'FRM_PF' in i)
                .map(item => item.FRM_PF),
            ),
          )
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.SI_PIC?.startsWith(value as string);
          },
        },
      ] as TableColumnsType<ICommunityImages>,
    [tab, selected],
  );
  const farmer_images = data?.farmer_upload;
  const community_images = data?.community_upload;

  const insert = useMemo(() => {
    const listMap: Record<
      'farmer_upload' | 'community_upload',
      (IFarmerImages | ICommunityImages)[] | undefined
    > = {
      farmer_upload: farmer_images,
      community_upload: community_images,
    };
    return listMap[tab]?.map(c => ({ ...c, _key: uniqueId('b-') })) || [];
  }, [tab, farmer_images, community_images]);

  useEffect(() => {
    setSelected(insert);
    setSelectedRowKeys([]);
  }, [insert]);

  const rowSelection = {
    onChange: (
      _: React.Key[],
      selectedRows: (IFarmerImages | ICommunityImages)[],
    ) => {
      setSelectedRowKeys(selectedRows.map(row => row._key));
      setSelectedRows(selectedRows);
    },
    selectedRowKeys,
    getCheckboxProps: (record: IFarmerImages | ICommunityImages) => ({
      disabled: false,
    }),
  };

  const [search, SetSearch] = useState('');

  const filteredData = useMemo(() => {
    return selected.filter(item => {
      let farmerCode = '';
      if ('FRM_CODE' in item && typeof item.FRM_CODE === 'string') {
        farmerCode = item.FRM_CODE.toLowerCase();
      }
      const searchValue = search.trim().toLowerCase();
      return !searchValue || farmerCode.includes(searchValue);
      // }
    });
  }, [selected, action, search]);

  return (
    <div>
      <Flex justify="space-between" gap={2} className="mb-3">
        <Flex gap={0}>
          <Radio.Group value={tab} onChange={e => setTab(e.target.value)}>
            <Radio.Button value="farmer_upload">Farmer</Radio.Button>
            <Radio.Button value="community_upload">Community</Radio.Button>
          </Radio.Group>
        </Flex>
        <Flex gap={12}>
          <button
            type="button"
            className="btn btn-success"
            style={{ minWidth: 100 }}
            disabled={!selectedRowKeys.length}
            onClick={() => handleSyncSelectedImages(selectedRows)}
          >
            {actionImagesMutation.isPending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
                <span className="sr-only">Loading...</span>
              </>
            ) : (
              'ส่งข้อมูลไป OTOD >'
            )}
          </button>
        </Flex>
      </Flex>
      <Table<IFarmerImages | ICommunityImages>
        rowSelection={{ type: 'checkbox', ...rowSelection }}
        columns={
          (tab === 'farmer_upload'
            ? columnsFarmer
            : columnsCommunity) as TableColumnsType<
            IFarmerImages | ICommunityImages
          >
        }
        dataSource={filteredData}
        size="small"
        bordered
        rowKey={r => r._key}
        pagination={false}
        locale={{ emptyText: 'ไม่พบรายการ' }}
      />
      {/* <Suspense>
        <Modal
          open={modal.open}
          width={350}
          onCancel={() => setModal({ ..._modal })}
          // loading={matchBrandApi.isPending}
          // onOk={() => {
          //   form
          //     .validateFields()
          //     .then(values => {
          //       onFinish(values);
          //     })
          //     .catch(() => {
          //       // validation failed
          //     });
          // }}
        >
        <Form
          form={form}
          name="farmer"
          layout="vertical"
          // onFinish={onFinish}
          autoComplete="off"
        >
          <Form.Item<IFarmerImages | ICommunityImages>
            label="รหัส"
            name="FRM_CODE"
            rules={[{ required: true }]}
          >
            <Select
              style={{ minWidth: 150 }}
              size="large"
              showSearch
              allowClear
              placeholder="ค้นหา"
              filterOption={(input, option) =>
                ((option?.label as string) ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={[...(insert || [])].sort().map(frm => ({
                label: `${frm.FRM_CODE} - ${frm.FRM_CODE}`,
                value: frm.FRM_CODE,
              }))}
            />
          </Form.Item>
        </Form>
        </Modal>
      </Suspense> */}
    </div>
  );
};

export default UploadImagesPage;
